import { type FC, useEffect, useState } from 'react';
import { PieChart, Pie, Tooltip, ResponsiveContainer, Legend, type LegendPayload, type PieLabelRenderProps } from 'recharts';
import { DEFAULT_CHART_COLOR } from '../Chart';
import { BREAKPOINT_MD } from 'utils/breakpoint';


export type PieChartData = Partial<Record<string, unknown>> & {
    color?: string;
};

interface PieChartComponentProps {
    chartData?: PieChartData[];
    chartValueLabel?: string;
    showLegend?: boolean;
}

const DESKTOP_MEDIA_QUERY = `(min-width: ${BREAKPOINT_MD}px)`;
const RADIAN = Math.PI / 180;
// Distance from the centre to the label, as a fraction of the radius.
const LABEL_RADIUS_RATIO = 0.8;
const LABEL_FONT_SIZE_DESKTOP = 18;
const LABEL_FONT_SIZE_MOBILE = 14;
// Segments below this value are too thin to fit a label. Their value is still shown in the tooltip.
const LABEL_MIN_VALUE = 5;
const TOOLTIP_TEXT_COLOR = '#000000';
// Appended to values in segment labels and the tooltip.
const VALUE_UNIT = '%';
const LABEL_TEXT_LIGHT = '#ffffff';
// $cbus-bright-blue-700
const LABEL_TEXT_DARK = '#00002d';

const useIsDesktop = (): boolean => {
    const [isDesktop, setIsDesktop] = useState(() => {
        return window.matchMedia(DESKTOP_MEDIA_QUERY).matches;
    });

    useEffect(() => {
        const mediaQueryList = window.matchMedia(DESKTOP_MEDIA_QUERY);
        const handleChange = (e: MediaQueryListEvent): void => {
            setIsDesktop(e.matches);
        };

        mediaQueryList.addEventListener('change', handleChange);

        return () => {
            mediaQueryList.removeEventListener('change', handleChange);
        };
    }, []);

    return isDesktop;
};

// Picks light or dark label text depending on the segment colour, so values stay legible on pale segments.
const getLabelTextColor = (fill?: string): string => {
    const hex = fill?.match(/^#?([0-9a-f]{6})$/i)?.[1];

    if (!hex) {
        return LABEL_TEXT_LIGHT;
    }

    const [r, g, b] = [0, 2, 4].map(i => {
        return parseInt(hex.slice(i, i + 2), 16);
    });
    const luminance = (0.299 * r) + (0.587 * g) + (0.114 * b);

    return luminance > 160 ? LABEL_TEXT_DARK : LABEL_TEXT_LIGHT;
};

const renderSegmentLabel = (props: PieLabelRenderProps, fontSize: number, unit?: string): JSX.Element | null => {
    const { cx, cy, midAngle = 0, innerRadius = 0, outerRadius = 0, value, fill } = props;

    if (Number(value) < LABEL_MIN_VALUE) {
        return null;
    }

    const radius = Number(innerRadius) + ((Number(outerRadius) - Number(innerRadius)) * LABEL_RADIUS_RATIO);
    const x = Number(cx) + (radius * Math.cos(-midAngle * RADIAN));
    const y = Number(cy) + (radius * Math.sin(-midAngle * RADIAN));

    return (
        <text
            x={x}
            y={y}
            fill={getLabelTextColor(fill)}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={fontSize}
            fontWeight="bold"
            pointerEvents="none"
        >
            {value}{unit ? unit : ''}
        </text>
    );
};

const formatTooltipValue = (value: unknown, unit?: string): string => {
    return `${value}${unit ? unit : ''}`;
};

const PieChartComponent: FC<PieChartComponentProps> = props => {
    const {
        chartData,
        chartValueLabel,
        showLegend = true,
    } = props;

    const isDesktop = useIsDesktop();

    if (!chartData) {
        return null;
    }

    // Recharts sorts legend items alphabetically by default, so keep them in data order instead.
    const sortLegendByDataOrder = (item: LegendPayload): number => {
        return chartData.findIndex(entry => {
            return entry.name === item.value;
        });
    };

    const reformattedChartData = chartData.map(entry => {
        return {
            ...entry,
            // value: Number(entry.value),
            fill: entry.color || DEFAULT_CHART_COLOR,
        };
    });

    return (
        <div className="pie-chart-component">
            <ResponsiveContainer>
                <PieChart>
                    <Pie
                        dataKey={chartValueLabel ? chartValueLabel : 'value'}
                        data={reformattedChartData}
                        outerRadius="90%"
                        // Start at 12 o'clock and go clockwise
                        startAngle={90}
                        endAngle={-270}
                        label={(labelProps): JSX.Element | null => {
                            return renderSegmentLabel(labelProps, isDesktop ? LABEL_FONT_SIZE_DESKTOP : LABEL_FONT_SIZE_MOBILE, VALUE_UNIT);
                        }}
                        labelLine={false}
                    />
                    <Tooltip
                        itemStyle={{ color: TOOLTIP_TEXT_COLOR }}
                        formatter={(value): string => {
                            return formatTooltipValue(value, VALUE_UNIT);
                        }}
                    />
                    {showLegend && (
                        isDesktop ? (
                            <Legend
                                layout="vertical"
                                position="right"
                                itemSorter={sortLegendByDataOrder}
                            />
                        ) : (
                            <Legend
                                layout="horizontal"
                                position="bottom"
                                wrapperStyle={{ paddingTop: '16px' }}
                                itemSorter={sortLegendByDataOrder}
                            />
                        )
                    )}
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
};

export default PieChartComponent;
